import React, { useEffect, useRef, useState } from "react";

import { Box, Pagination, Stack, Typography, IconButton } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useAppSelector } from "../../../../../store";
import { getFullInvitationsState, resetMyComment } from "../../../../../store/slices/invitations/invitations.slice";
import {
	useDeleteCommentMutation,
	useLazyGetWeddingInvitationCommentsQuery
} from "../../../../../store/services/invitations/invitations.service";
import { useParams, useSearchParams } from "react-router-dom";
import { Delete, CheckCircle, Cancel } from "@mui/icons-material";
import { deleteAnonId, getOrCreateAnonId } from "../../../../../utils/getOrCreateAnonId";
import { IComment, INVITATION_RESPONSE_TYPE } from "../../../../../store/services/invitations/dto/invitations.dto";
import ConfirmModal from "../../../../Modals/ConfirmModal";
import ReCAPTCHA from "react-google-recaptcha";
import { notifyError } from "../../../../../utils/NotifyError";
import { IError } from "../../../../../utils/getErrorMessage";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../../../store";
import { LANG_AR, LTR, RTL } from "../../constants";
import { ar } from "date-fns/locale";
import { convertDigitsToArabicNumerals } from "../../../../../utils/formatNumbersToArabic";
import { CommentsWrapper } from "./styles";
import { TemplateColorObject } from "../../../../../InvitationColorMatrix";

const COMMENTS_PER_PAGE = 10;
const Comments = ({
	canEdit,
	language,
	colormatrix
}: {
	canEdit: boolean;
	language: string;
	colormatrix: TemplateColorObject;
}) => {
	const { invitationComments, totalComments = 10, commentsPage, myComment } = useAppSelector(getFullInvitationsState);
	const params = useParams();
	const dispatch = useAppDispatch();
	const [deleteCommentId, setDeleteCommentId] = useState<null | string>(null);
	const id = params.id;
	const recaptchaRef = useRef<ReCAPTCHA>(null);
	const [searchParams] = useSearchParams();
	const password = searchParams.get("password");
	const [fetchComments] = useLazyGetWeddingInvitationCommentsQuery();
	const [deleteComment, { isLoading: isDeletingComment }] = useDeleteCommentMutation();
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const anonymous_token = getOrCreateAnonId(id as string);
	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		fetchComments({ id: id as string, password: password as string, page: value });
	};
	useEffect(() => {
		return () => {
			if (recaptchaRef.current) recaptchaRef.current.reset();
		};
	}, []);
	const confirmCommentDelete = (comment: IComment) => {
		setDeleteCommentId(comment.anonymous_token);
	};
	const handleDeleteComment = async () => {
		if (canEdit) {
			return;
		}
		if (!recaptchaRef.current) {
			return notifyError({ error: "Recaptcha failed" } as IError);
		}
		setIsDeleteLoading(true);
		let recaptchaToken: string | null = "";
		try {
			recaptchaToken = await recaptchaRef.current.executeAsync();
		} catch (e) {
			console.error(e);
		}

		deleteComment({
			_id: id as string,
			recaptchaToken: recaptchaToken as string,
			anonymous_token: deleteCommentId as string
		}).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setDeleteCommentId(null);
				deleteAnonId(id as string);
				dispatch(resetMyComment());
				toast.success("Response deleted successfully!", { autoClose: 3000 });
			}
			if (error) {
				notifyError(error as IError);
			}
			setIsDeleteLoading(false);
		});
	};

	const formatCommentDate = (date: Date, language: string) => {
		if (language === LANG_AR) {
			return convertDigitsToArabicNumerals(
				formatDistanceToNow(date, {
					addSuffix: true,
					locale: language === LANG_AR ? ar : undefined
				})
			);
		}

		return formatDistanceToNow(date, {
			addSuffix: true
		});
	};

	return (
		<CommentsWrapper colormatrix={colormatrix}>
			{myComment && (
				<Box className={`myComment ${language === LANG_AR ? RTL : LTR}`} m={2}>
					<Box display={"flex"} justifyContent={"space-between"} gap={1}>
						<Box display={"flex"} alignItems={"center"} gap={1}>
							<Typography fontWeight={"bold"}>{myComment.name}</Typography>
							{myComment.response === INVITATION_RESPONSE_TYPE.COMING ? (
								<CheckCircle className={"accept_icon"} />
							) : (
								<Cancel className={"reject_icon"} />
							)}
							<Typography fontSize={12}>{formatCommentDate(new Date(myComment.createdAt), language)}</Typography>
						</Box>
						{myComment.anonymous_token === anonymous_token && (
							<IconButton onClick={() => confirmCommentDelete(myComment)} className={"delete_icon"}>
								<Delete />
							</IconButton>
						)}
					</Box>
					<Box>
						{myComment.comment && <Typography style={{ wordBreak: "break-word" }}>{myComment.comment}</Typography>}
					</Box>
				</Box>
			)}
			{(invitationComments || []).length > 0 ? (
				<Box
					className={`comments_wrapper ${language === LANG_AR ? RTL : LTR}`}
					alignSelf={language === LANG_AR ? "flex-end" : "flex-start"}
				>
					{(invitationComments || []).map((comment, i) => (
						<Box className={`comment ${language === LANG_AR ? RTL : LTR}`} key={comment._id}>
							<Box className={"name_wrapper"}>
								<Typography fontWeight={"bold"} style={{ maxWidth: 190 }} noWrap>
									{comment.name}
								</Typography>
								{comment.response === INVITATION_RESPONSE_TYPE.COMING ? (
									<CheckCircle className={"accept_icon"} />
								) : (
									<Cancel className={"reject_icon"} />
								)}
								<Typography fontSize={12}>{formatCommentDate(new Date(comment.createdAt), language)}</Typography>
							</Box>
							{comment.comment && (
								<Typography className={`${language === LANG_AR ? RTL : LTR}`} style={{ wordBreak: "break-word" }}>
									{comment.comment}
								</Typography>
							)}
						</Box>
					))}
					{totalComments > COMMENTS_PER_PAGE && (
						<Box my={4}>
							<Stack spacing={2}>
								<Pagination
									count={Math.ceil(totalComments / COMMENTS_PER_PAGE)}
									onChange={handlePageChange}
									page={commentsPage}
									siblingCount={1}
									boundaryCount={1}
								/>
							</Stack>
						</Box>
					)}
					{!!deleteCommentId && (
						<ConfirmModal
							open={!!deleteCommentId}
							title={"Delete comment"}
							message={"Are you sure you want to delete your comment?"}
							onConfirm={handleDeleteComment}
							onClose={() => setDeleteCommentId(null)}
							confirmText={"Yes, Delete"}
							cancelText={"No, Cancel"}
							loading={isDeletingComment || isDeleteLoading}
						/>
					)}
					<ReCAPTCHA
						sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
						size="invisible"
						ref={recaptchaRef}
						badge="bottomright"
					/>
				</Box>
			) : (
				<Box />
			)}
		</CommentsWrapper>
	);
};

export default Comments;
